import { DetailViewCard } from '../CardWithTitle';
import { useEffect, useState } from 'react';
import {
  getLineItemLoadingSubject,
  getLineItemProductLabel,
  getLineItemSubject,
  getLineItemTotal,
  LineItem,
  useLineItemsStore
} from '../../../store/LineItem';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { DetailsLineItemsCTA } from '../DetailsLineItemsCTA/DetailsLineItemsCTA';
import { LineItemModal } from './LineItemModal';
import { BasicDataGrid } from '../../Compounds/Table/BasicDataGrid';
import { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { Text } from '../../Atoms/Text';
import { Icon } from '../../Atoms/Icon';
import { NavLink } from 'react-router-dom';
import { isConvertedRecord } from '../../../utils/originLabel';
import { getUser, isInternalUser, isInventoryFull } from '../../../store/User';

const DRAWER_LINE_ITEM_LIMIT = 5;

export interface DetailsLineItemsProps {
  id: string;
  mrc: number;
  entryMethod?: string;
}

export function DetailsLineItems({ id, mrc, entryMethod }: DetailsLineItemsProps) {
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const { fetchLineItems, createLineItem, deleteLineItem, updateLineItem } = useLineItemsStore();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [editedLineItem, setEditedLineItem] = useState<LineItem | null>(null);
  const mrcRequired = !isConvertedRecord(entryMethod);
  const user = getUser();
  const canEditLineItems = !isConvertedRecord(entryMethod) || (isInternalUser(user) && isInventoryFull(user));

  useEffect(() => {
    const lineItemSub = getLineItemSubject().subscribe((lineItems) => setLineItems(lineItems));
    const lineItemLoadingSub = getLineItemLoadingSubject().subscribe((loading) => setIsFetching(loading));

    return () => {
      if (lineItemSub) lineItemSub.unsubscribe();
      if (lineItemLoadingSub) lineItemLoadingSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (id) fetchLineItems(id);
  }, [id]);

  const cardActions = () => {
    if (!canEditLineItems) return undefined;
    const handleModalOpen = () => {
      if (collapsed) setCollapsed(false);
      setIsModalOpen(true);
    };
    return <DetailsLineItemsCTA setIsModalOpen={handleModalOpen} />;
  };

  const handleSend = (lineItem: LineItem) => {
    createLineItem({
      id: '',
      outside_inventory_id: id,
      product_id: lineItem.product_id,
      description: lineItem.description,
      mrc: lineItem.mrc,
      nrc: lineItem.nrc
    });
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    setEditedLineItem(null);
    await deleteLineItem(id);
    setIsDeleting(false);
  };

  const handleEdit = (row: LineItem) => {
    setEditedLineItem(row);
  };

  const handleCancelEdit = () => {
    setEditedLineItem(null);
  };

  const handleUpdateLineItem = (lineItem: LineItem) => {
    updateLineItem(lineItem);
    setEditedLineItem(null);
  };

  const renderLineItems = () => {
    const columns: GridColDef[] = [
      {
        field: 'productDisplay',
        headerName: 'Product',
        valueGetter: (_value, row) => {
          if (row.id === 'total') return 'Total';
          return getLineItemProductLabel(row?.product_id);
        },
        flex: 1.5,
        minWidth: 110
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1.5,
        minWidth: 120,
        renderCell: (params) => {
          return params.row.id === 'total' ? '' : `${params.value}`;
        }
      },
      {
        field: 'mrc',
        headerName: 'Monthly Cost',
        flex: 1,
        minWidth: 90,
        renderCell: (params) => {
          return params.row.id === 'total' ? `$${params.row.totalCost}` : `$${params.value || 0}`;
        }
      },
      {
        field: 'nrc',
        headerName: 'One Time Cost',
        flex: 1,
        minWidth: 90
      },
      ...(canEditLineItems
        ? [
            {
              field: 'action',
              sortable: false,
              headerName: 'Actions',
              renderCell: (params: GridCellParams) => {
                if (params.row.id === 'total') return null;
                return (
                  <div className="flex space-x-3">
                    <button
                      disabled={isDeleting}
                      onClick={() => handleEdit(params.row)}>
                      <Text
                        size="sm"
                        color="indigo">
                        Edit
                      </Text>
                    </button>
                    <button
                      disabled={isDeleting}
                      onClick={() => handleDelete(params.row.id)}>
                      <Text
                        size="sm"
                        color="indigo">
                        Delete
                      </Text>
                    </button>
                  </div>
                );
              },
              hideable: false,
              width: 120
            }
          ]
        : [])
    ];

    const totalCost = getLineItemTotal();
    const totalDelta = Math.abs(totalCost - mrc);
    const totalRow = { id: 'total', productDisplay: 'Total', totalCost, mrc: '', nrc: '', action: null };

    return (
      <div>
        <BasicDataGrid
          rows={[...lineItems.slice(0, DRAWER_LINE_ITEM_LIMIT), totalRow]}
          columns={columns}
        />
        {totalDelta > 0 && !isConvertedRecord(entryMethod) && (
          <div className="mt-4 p-3 border-t">
            <div className="flex items-start">
              <Icon
                type="info"
                className="p-2 mr-2"
              />
              <div className="flex flex-col text-xs">
                <strong>Line items do not match record total</strong>
                <div className="mt-2">
                  The total monthly cost for all line items has a difference of{' '}
                  <strong>${totalDelta.toFixed(2)} </strong>
                  than the amount entered in this record’s inventory details.
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="mt-4">
          <NavLink
            to={`/line-items?recordId=${id}`}
            className="text-indigo text-sm">
            All Line Items
          </NavLink>
        </div>
      </div>
    );
  };

  return (
    <>
      <DetailViewCard
        cardActions={cardActions()}
        collapsed={collapsed}
        onCollapseClick={setCollapsed}
        enableCollapse
        title={`Line Items (${lineItems?.length || 0})`}
        childrenWrapperClass="overflow-x-auto">
        <div className="font-sans text-black">
          {isFetching && <ComponentSpinner />}
          {!isFetching && !lineItems?.length && <div className="text-center">No line items</div>}
          {!isFetching && !!lineItems?.length && renderLineItems()}
        </div>
      </DetailViewCard>
      {editedLineItem && (
        <LineItemModal
          lineItem={editedLineItem || ({} as LineItem)}
          handleCancel={handleCancelEdit}
          handleSend={handleUpdateLineItem}
          isModalOpen={!!editedLineItem}
          mrcRequired={mrcRequired}
        />
      )}
      <LineItemModal
        lineItem={{ id: '', outside_inventory_id: id, product_id: '', description: '', mrc: 0, nrc: 0 }}
        handleCancel={handleCancel}
        handleSend={handleSend}
        isModalOpen={isModalOpen}
        mrcRequired={mrcRequired}
      />
    </>
  );
}
